/**
 * Helpers for form date-field defaults / min / max (static, dynamic ~tokens~, variable + offset).
 */

export const DATE_OFFSET_UNITS = [
  { value: "days", label: "Days" },
  { value: "business_days", label: "Business Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
]

export const DATE_EXPRESSION_MODES = [
  { value: "none", label: "None" },
  { value: "static", label: "Static" },
  { value: "dynamic", label: "Dynamic" },
  { value: "variable", label: "Variable" },
]

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * @param {unknown} offset
 * @returns {{ amount: number, unit: string } | null}
 */
export function normalizeDateOffset(offset) {
  if (!offset || typeof offset !== "object") return null
  const amount = Number(offset.amount)
  if (!Number.isFinite(amount) || amount === 0) return null
  const unit = String(offset.unit || "days").trim() || "days"
  const allowed = DATE_OFFSET_UNITS.map((u) => u.value)
  return {
    amount: Math.trunc(amount),
    unit: allowed.includes(unit) ? unit : "days",
  }
}

/**
 * Infer editor mode from a stored expression + optional offset.
 * @param {string|null|undefined} value
 * @param {unknown} offset
 */
export function inferDateExpressionMode(value, offset) {
  const v = value == null ? "" : String(value).trim()
  if (!v) return "none"
  if (v.includes("{{") || v.includes("{") || v.startsWith("$")) return "variable"
  if (v.includes("~")) return "dynamic"
  if (ISO_DATE_RE.test(v)) return "static"
  if (normalizeDateOffset(offset)) return "variable"
  return "static"
}

/**
 * Strip outer ~ from a dynamic date token, e.g. ~tomorrow~ → tomorrow
 * @param {string} value
 */
export function extractDynamicDateToken(value) {
  const v = value == null ? "" : String(value).trim()
  const m = v.match(/^~([a-zA-Z0-9_-]+)~$/)
  return m ? m[1] : ""
}

/**
 * Map DynamicDateSelector option (+ custom) to a ~token~ string.
 * @param {string} option
 * @param {number} customDays
 * @param {string} customPeriod
 */
export function dynamicOptionToToken(option, customDays = 1, customPeriod = "days") {
  if (!option || option === "custom") {
    if (customPeriod === "day_of_month") {
      return `~custom_day_of_month_${customDays}~`
    }
    return `~custom_${customDays}_${customPeriod}~`
  }
  return `~${option}~`
}

/**
 * Parse a ~custom_*~ token back into DynamicDateSelector state.
 * @param {string} tokenInner e.g. custom_7_days or tomorrow
 */
export function parseDynamicTokenInner(tokenInner) {
  const inner = String(tokenInner || "")
  if (inner.startsWith("custom_day_of_month_")) {
    const day = parseInt(inner.replace("custom_day_of_month_", ""), 10)
    return {
      option: "custom",
      customDays: Number.isFinite(day) ? day : 1,
      customPeriod: "day_of_month",
      showCustomInput: true,
    }
  }
  const customMatch = inner.match(
    /^custom_(\d+)_(day|days|week|weeks|month|months|quarter|quarters|business_day|business_days)$/,
  )
  if (customMatch) {
    return {
      option: "custom",
      customDays: parseInt(customMatch[1], 10) || 1,
      customPeriod: customMatch[2],
      showCustomInput: true,
    }
  }
  return {
    option: inner || "today",
    customDays: 1,
    customPeriod: "days",
    showCustomInput: false,
  }
}

/**
 * True when value is a concrete YYYY-MM-DD suitable for <input type="date">.
 * @param {unknown} value
 */
export function isResolvedIsoDate(value) {
  return ISO_DATE_RE.test(String(value ?? "").trim())
}

/**
 * Validate a date field value against resolved minDate/maxDate.
 * @param {string|null|undefined} value YYYY-MM-DD
 * @param {{ minDate?: string, maxDate?: string }|null|undefined} validation
 * @returns {string|null} error message or null
 */
export function validateDateFieldRange(value, validation) {
  const v = value == null ? "" : String(value).trim()
  if (!v) return null
  if (!isResolvedIsoDate(v)) {
    return "Enter a valid date (YYYY-MM-DD)"
  }
  const minDate =
    validation?.minDate != null ? String(validation.minDate).trim() : ""
  const maxDate =
    validation?.maxDate != null ? String(validation.maxDate).trim() : ""
  if (minDate && isResolvedIsoDate(minDate) && v < minDate) {
    return `Date must be on or after ${minDate}`
  }
  if (maxDate && isResolvedIsoDate(maxDate) && v > maxDate) {
    return `Date must be on or before ${maxDate}`
  }
  return null
}

/**
 * Collect date-range errors for visible date fields.
 * @param {Array<{ id?: string, type?: string, validation?: object }>} fields
 * @param {Record<string, unknown>} formData
 * @param {(field: object) => boolean} isFieldVisibleFn
 * @returns {Record<string, string>}
 */
export function getDateFieldRangeErrors(fields, formData, isFieldVisibleFn) {
  const errors = {}
  if (!Array.isArray(fields)) return errors
  for (const field of fields) {
    if (!field?.id || field.type !== "date") continue
    if (typeof isFieldVisibleFn === "function" && !isFieldVisibleFn(field)) {
      continue
    }
    const err = validateDateFieldRange(formData[field.id], field.validation)
    if (err) errors[field.id] = err
  }
  return errors
}
